import "./application";
import {
  auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from '../utils/firebase/auth';
import {
  fetchSignInMethodsForEmail,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  OAuthProvider,
  sendSignInLinkToEmail
} from 'firebase/auth';
import { installClaimUnloadGuard, allowClaimNavigation } from '../utils/claimUnloadGuard';
import { claimAuthOptionsFor, SIGNUP_SUBMIT_LABEL, SIGNIN_SUBMIT_LABEL } from '../utils/claimAuthOptions';

declare global {
  interface Window {
    claimToken: string;
    claimAvailable: boolean;
    claimAlreadyDone: boolean;
    profileUrl: string;
    welcomeUrl: string;
    prefilledEmail: string;
    claimMode: 'signin' | 'signup';
    user: any;
  }
}

const form               = document.getElementById('claim_form') as HTMLFormElement | null;
const emailInput         = document.getElementById('claim_email') as HTMLInputElement | null;
const passwordBlock      = document.getElementById('claim_password_block') as HTMLElement | null;
const passwordInput      = document.getElementById('claim_password') as HTMLInputElement | null;
const submitBtn          = document.getElementById('claim_submit') as HTMLButtonElement | null;
const submitLabel        = document.getElementById('claim_submit_label') as HTMLElement | null;
const errorBox           = document.getElementById('claim_error') as HTMLElement | null;
const forgotLink         = document.getElementById('claim_forgot_password') as HTMLAnchorElement | null;
const providerLoading    = document.getElementById('claim_provider_loading') as HTMLElement | null;
const oauthBlock         = document.getElementById('claim_oauth_block') as HTMLElement | null;
const oauthDivider       = document.getElementById('claim_oauth_divider') as HTMLElement | null;
const googleBtn          = document.getElementById('claim_google') as HTMLButtonElement | null;
const appleBtn           = document.getElementById('claim_apple') as HTMLButtonElement | null;
const emailLinkLink      = document.getElementById('claim_email_link') as HTMLAnchorElement | null;
const webviewWarning     = document.getElementById('claim_webview_warning') as HTMLElement | null;
const copyUrlBtn         = document.getElementById('claim_copy_url') as HTMLButtonElement | null;
const changeEmailBtn     = document.getElementById('claim_change_email') as HTMLButtonElement | null;
const resetEmailBtn      = document.getElementById('claim_reset_email') as HTMLButtonElement | null;
const emailSwitchedHint  = document.getElementById('claim_email_switched') as HTMLElement | null;
const emailIcon          = document.getElementById('claim_email_icon') as HTMLElement | null;
const emailLabelText     = document.getElementById('claim_email_label_text') as HTMLElement | null;
const stepEmail          = document.getElementById('claim_step_email') as HTMLElement | null;

let mode: 'signup' | 'signin' = (window.claimMode === 'signin') ? 'signin' : 'signup';

// The address the Stripe charge was made under. The buyer can retarget the claim
// to another one; everything downstream reads the input, never this.
const paidWithEmail = window.prefilledEmail || '';

function currentEmail(): string {
  return (emailInput?.value || '').trim();
}

function emailWasChanged(): boolean {
  return currentEmail().toLowerCase() !== paidWithEmail.trim().toLowerCase();
}

function setError(message: string, level: 'danger' | 'info' = 'danger') {
  if (!errorBox) return;
  errorBox.textContent = message;
  errorBox.classList.remove('d-none', 'alert-danger', 'alert-info');
  errorBox.classList.add(level === 'danger' ? 'alert-danger' : 'alert-info');
}

function clearError() {
  if (!errorBox) return;
  errorBox.textContent = '';
  errorBox.classList.add('d-none');
}

function show(el: HTMLElement | null)  { el?.classList.remove('d-none'); }
function hide(el: HTMLElement | null)  { el?.classList.add('d-none'); }

// All auth controls — buttons + links. Disabled wholesale during any in-flight
// sign-in attempt so a user can't kick off two parallel Firebase auth flows
// (e.g. tap Google while a password submit is mid-flight). The triggering
// control gets a spinner; everything else dims to 0.5 opacity.
function authControls(): (HTMLButtonElement | HTMLAnchorElement)[] {
  return [submitBtn, googleBtn, appleBtn, forgotLink, emailLinkLink].filter(
    (el): el is HTMLButtonElement | HTMLAnchorElement => !!el
  );
}

function lockAuth(active: HTMLElement | null, busyLabel?: string) {
  authControls().forEach((c) => {
    if (c === active) return;
    if (c instanceof HTMLButtonElement) c.disabled = true;
    c.style.pointerEvents = 'none';
    c.style.opacity = '0.5';
  });
  if (passwordInput) passwordInput.disabled = true;
  if (active instanceof HTMLButtonElement) {
    active.disabled = true;
    if (busyLabel && !active.dataset.originalHtml) {
      active.dataset.originalHtml = active.innerHTML;
      active.innerHTML = `<span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>${busyLabel}`;
    }
  }
}

function unlockAuth(active: HTMLElement | null) {
  authControls().forEach((c) => {
    if (c instanceof HTMLButtonElement) c.disabled = false;
    c.style.pointerEvents = '';
    c.style.opacity = '';
  });
  if (passwordInput) passwordInput.disabled = false;
  if (active instanceof HTMLButtonElement && active.dataset.originalHtml) {
    active.innerHTML = active.dataset.originalHtml;
    delete active.dataset.originalHtml;
  }
}

// Detects Facebook/Instagram/TikTok/etc. in-app webviews. These typically block
// OAuth popups and redirects to Google, so we surface an "open in browser"
// callout when we know the user needs OAuth to sign in.
function isInAppBrowser(): boolean {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|FB_IAB|FB4A|Instagram|BytedanceWebview|TikTok|MicroMessenger|Line\/|; wv\)/i.test(ua);
}

async function sendAccessTokenToRails() {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('No Firebase ID token after auth');
  const response = await fetch(`/firebase/auth.json?accessToken=${encodeURIComponent(idToken)}`, {
    credentials: 'same-origin'
  });
  if (!response.ok) throw new Error(`Rails auth sync failed (${response.status})`);
}

async function finalizeClaimOnRails(): Promise<{ claimed: boolean; reason: string; redirect_url?: string }> {
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';
  const response = await fetch('/purchases/claim', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken
    },
    body: JSON.stringify({ claim_token: window.claimToken })
  });
  return response.json().catch(() => ({ claimed: false, reason: 'parse_error' }));
}

function nextUrlAfterClaim(claim: { redirect_url?: string }): string {
  return claim.redirect_url || window.welcomeUrl || window.profileUrl || '/';
}

// Reasons the buyer can act on. Anything else is a server-side problem and a
// reload is the honest response — the page re-renders whatever state Rails is
// actually in rather than us guessing.
const CLAIM_REASON_MESSAGES: Record<string, string> = {
  already_subscribed:
    'That account already has an active subscription. Use an account without one, ' +
    'or contact info@hiiker.com and we can move this purchase across.',
  owned_by_other: 'This purchase has already been activated on another account. Contact info@hiiker.com and we can help.',
  expired: 'This claim link has expired. Contact info@hiiker.com and we will get your subscription set up.'
};

async function completeAfterFirebaseAuth() {
  await sendAccessTokenToRails();
  const claim = await finalizeClaimOnRails();

  if (claim.claimed || claim.reason === 'already_claimed') {
    allowClaimNavigation();
    window.location.href = nextUrlAfterClaim(claim);
    return;
  }

  const message = CLAIM_REASON_MESSAGES[claim.reason];
  if (message) {
    setError(message);
    unlockAuth(submitBtn);
    return;
  }

  allowClaimNavigation();
  window.location.reload();
}

// Applies a decided set of options to the DOM. The decision itself lives in
// utils/claimAuthOptions so it can be unit-tested away from Firebase.
function applyAuthOptions(methods: string[]) {
  const opts = claimAuthOptionsFor(methods, { emailChanged: emailWasChanged() });

  mode = opts.mode;
  if (submitLabel) submitLabel.textContent = opts.submitLabel;

  opts.showPassword ? show(passwordBlock) : hide(passwordBlock);
  opts.showSubmit   ? show(submitBtn)     : hide(submitBtn);
  opts.showForgot   ? show(forgotLink)    : hide(forgotLink);
  opts.showGoogle   ? show(googleBtn)     : hide(googleBtn);
  opts.showApple    ? show(appleBtn)      : hide(appleBtn);
  opts.showEmailLink ? show(emailLinkLink) : hide(emailLinkLink);
  opts.showOauthDivider ? show(oauthDivider) : hide(oauthDivider);
  (opts.showGoogle || opts.showApple) ? show(oauthBlock) : hide(oauthBlock);

  // Webview warning only matters when OAuth is the only way through.
  const oauthOnly = !opts.showPassword && (opts.showGoogle || opts.showApple);
  if (oauthOnly && isInAppBrowser()) show(webviewWarning); else hide(webviewWarning);
}

async function fetchMethodsFor(email: string): Promise<string[]> {
  try {
    return await fetchSignInMethodsForEmail(auth, email);
  } catch (e) {
    return [];
  }
}

// Provider detection — on load for returning users, and again whenever the
// buyer retargets the claim to a different address, so the controls always match
// the account that address actually has.
async function detectProvidersAndRenderUI() {
  const email = currentEmail();
  if ((mode !== 'signin' && !emailWasChanged()) || !email) {
    hide(providerLoading);
    return;
  }
  show(providerLoading);
  const methods = await fetchMethodsFor(email);
  hide(providerLoading);
  applyAuthOptions(methods);
}

async function handleSubmit(event: Event) {
  event.preventDefault();
  if (!emailInput || !passwordInput) return;
  clearError();

  const email = emailInput.value.trim();
  const password = passwordInput.value;

  if (!email) { setError('Email is missing.'); return; }
  if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

  lockAuth(submitBtn, mode === 'signup' ? 'Activating…' : 'Signing in…');

  try {
    if (mode === 'signup') {
      try {
        await createUserWithEmailAndPassword(auth, email, password);
      } catch (err: any) {
        if (err?.code === 'auth/email-already-in-use') {
          unlockAuth(submitBtn);
          mode = 'signin';
          if (submitLabel) submitLabel.textContent = SIGNIN_SUBMIT_LABEL;
          show(forgotLink);
          setError("Looks like you already have a HiiKER account with this email. Enter your existing password to activate your subscription.");
          return;
        }
        throw err;
      }
    } else {
      await signInWithEmailAndPassword(auth, email, password);
    }

    await completeAfterFirebaseAuth();
  } catch (err: any) {
    console.error('Claim activation failed', err);
    const code = err?.code as string | undefined;
    if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
      setError("That password didn't match. Try again, use a sign-in option below, or reset your password.");
      show(forgotLink);
    } else if (code === 'auth/weak-password') {
      setError('Password must be at least 6 characters.');
    } else if (code === 'auth/too-many-requests') {
      setError('Too many attempts. Try again in a few minutes or reset your password.');
      show(forgotLink);
    } else {
      setError(err?.message || 'Something went wrong. Try again or contact info@hiiker.com.');
    }
    unlockAuth(submitBtn);
  }
}

async function handleOAuth(provider: 'google' | 'apple') {
  clearError();
  const btn = provider === 'google' ? googleBtn : appleBtn;
  if (isInAppBrowser()) {
    show(webviewWarning);
    setError('In-app browsers block Google and Apple sign-in. Open this page in your default browser to continue.');
    return;
  }
  lockAuth(btn, 'Signing in…');
  try {
    const authProvider =
      provider === 'google'
        ? new GoogleAuthProvider()
        : (() => { const p = new OAuthProvider('apple.com'); p.addScope('email'); p.addScope('name'); return p; })();
    if (provider === 'google') {
      (authProvider as GoogleAuthProvider).setCustomParameters({ login_hint: currentEmail() });
    }

    try {
      await signInWithPopup(auth, authProvider);
    } catch (popupErr: any) {
      // Fall back to redirect if the popup was blocked. We deliberately leave
      // controls locked here — the page is about to navigate to the OAuth IDP.
      if (popupErr?.code === 'auth/popup-blocked' || popupErr?.code === 'auth/popup-closed-by-user') {
        allowClaimNavigation();
        await signInWithRedirect(auth, authProvider);
        return;
      }
      throw popupErr;
    }

    await completeAfterFirebaseAuth();
  } catch (err: any) {
    console.error('OAuth claim failed', err);
    setError(err?.message || `Could not sign in with ${provider}. Try again or use a different option.`);
    unlockAuth(btn);
  }
}

async function handleForgotPassword(event: Event) {
  event.preventDefault();
  if (!emailInput) return;
  const email = emailInput.value.trim();
  if (!email) return;
  try {
    await sendPasswordResetEmail(auth, email);
    setError(`Password reset email sent to ${email}. It can take a minute to arrive — check your spam folder if you don't see it, then come back here.`, 'info');
  } catch (err: any) {
    setError(err?.message || 'Could not send reset email.');
  }
}

async function handleEmailLink(event: Event) {
  event.preventDefault();
  if (!emailInput) return;
  const email = emailInput.value.trim();
  if (!email) return;
  lockAuth(emailLinkLink);
  try {
    await sendSignInLinkToEmail(auth, email, {
      url: window.location.href,
      handleCodeInApp: true
    });
    window.localStorage.setItem('emailForSignIn', email);
    setError(`Sign-in link sent to ${email}. Open it on this same device to finish activating your subscription.`, 'info');
  } catch (err: any) {
    setError(err?.message || 'Could not send sign-in link.');
  } finally {
    unlockAuth(emailLinkLink);
  }
}

async function handleCopyUrl() {
  try {
    await navigator.clipboard.writeText(window.location.href);
    if (copyUrlBtn) {
      const original = copyUrlBtn.textContent;
      copyUrlBtn.textContent = 'Copied — paste it in your browser';
      setTimeout(() => { if (copyUrlBtn && original) copyUrlBtn.textContent = original; }, 2500);
    }
  } catch {
    /* noop */
  }
}

// Already signed in (e.g. recovery-email click while authenticated) — kick the
// claim immediately and bounce.
async function autoClaimIfSignedIn() {
  if (!window.user || !window.claimAvailable || window.claimAlreadyDone) return;
  const claim = await finalizeClaimOnRails();

  if (claim.claimed || claim.reason === 'already_claimed') {
    allowClaimNavigation();
    window.location.href = nextUrlAfterClaim(claim);
    return;
  }

  // Never reload on a reason the buyer has to read — that would loop.
  const message = CLAIM_REASON_MESSAGES[claim.reason];
  if (message) { setError(message); return; }

  allowClaimNavigation();
  window.location.reload();
}

// --- Retargeting the claim to a different address ------------------------
// PurchaseClaimService keys off claim_token and never compares emails, so the
// purchase can legitimately land on any account. Unlocking the field is the
// whole mechanism; everything else here is keeping the page honest about which
// address PRO+ is about to be activated on.

function renderEmailTarget() {
  const switched = emailWasChanged();
  if (emailIcon) emailIcon.textContent = switched ? 'alternate_email' : 'lock';
  if (emailLabelText) {
    emailLabelText.textContent = switched ? 'Activate PRO+ on this email' : 'The email you paid with';
  }
  switched ? show(emailSwitchedHint) : hide(emailSwitchedHint);

  // Step 1 names the address too, so it has to track the field — otherwise it
  // keeps advertising the old address while the buyer types a new one. Between
  // clearing and typing there is no address to name yet.
  if (stepEmail) {
    if (currentEmail()) {
      stepEmail.textContent = currentEmail();
    } else {
      stepEmail.textContent = emailInput?.readOnly ? paidWithEmail : 'the address you enter below';
    }
  }
}

function unlockEmail() {
  if (!emailInput) return;
  clearError();
  emailInput.readOnly = false;
  emailInput.value = '';
  hide(changeEmailBtn);
  renderEmailTarget();
  emailInput.focus();

  // Nothing is known about an empty field yet: offer account creation, and let
  // detection (or the email-already-in-use fallback) correct it on entry.
  mode = 'signup';
  if (submitLabel) submitLabel.textContent = SIGNUP_SUBMIT_LABEL;
  show(passwordBlock);
  show(submitBtn);
  hide(forgotLink);
  hide(oauthBlock);
  hide(oauthDivider);
}

function relockEmail() {
  if (!emailInput) return;
  clearError();
  emailInput.value = paidWithEmail;
  emailInput.readOnly = true;
  show(changeEmailBtn);
  renderEmailTarget();
  mode = (window.claimMode === 'signin') ? 'signin' : 'signup';
  if (submitLabel) {
    submitLabel.textContent = mode === 'signin' ? SIGNIN_SUBMIT_LABEL : SIGNUP_SUBMIT_LABEL;
  }
  detectProvidersAndRenderUI();
}

// Re-detect on change/blur rather than per keystroke — one Firebase lookup per
// address the buyer settles on, not one per character.
let lastDetectedEmail = paidWithEmail;
async function handleEmailSettled() {
  const email = currentEmail();
  renderEmailTarget();
  if (!email || email === lastDetectedEmail) return;
  if (!emailInput?.checkValidity()) return;
  lastDetectedEmail = email;
  await detectProvidersAndRenderUI();
}

if (form)         form.addEventListener('submit', handleSubmit);
if (changeEmailBtn) changeEmailBtn.addEventListener('click', unlockEmail);
if (resetEmailBtn)  resetEmailBtn.addEventListener('click', relockEmail);
if (emailInput) {
  emailInput.addEventListener('change', handleEmailSettled);
  emailInput.addEventListener('blur', handleEmailSettled);
}
if (forgotLink)   forgotLink.addEventListener('click', handleForgotPassword);
if (emailLinkLink) emailLinkLink.addEventListener('click', handleEmailLink);
if (googleBtn)    googleBtn.addEventListener('click', () => handleOAuth('google'));
if (appleBtn)     appleBtn.addEventListener('click', () => handleOAuth('apple'));
if (copyUrlBtn)   copyUrlBtn.addEventListener('click', handleCopyUrl);

// Closing the tab here leaves a paid-for subscription attached to nothing, so
// an accidental close is worth one confirmation. Browsers supply their own
// wording and only prompt once the buyer has interacted with the page.
installClaimUnloadGuard(() => ({
  claimAvailable: !!window.claimAvailable,
  claimAlreadyDone: !!window.claimAlreadyDone
}));

detectProvidersAndRenderUI();
autoClaimIfSignedIn();
